import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { SERVICES } from "@/data/services";
import { SERVICE_AREAS, breadcrumbSchema } from "@/lib/seo";

export default function Services() {
  return (
    <main data-testid="services-page" className="pt-40 pb-32 min-h-screen">
      <Seo
        title="Laser Engraving, UV Printing & Corporate Gift Services — Leduc & Edmonton | Ember & Steel Studio"
        description="Custom laser engraving, personalised drinkware, corporate gifts, bulk branded merchandise, UV printing, metal business cards, industrial asset tags and customer-supplied item engraving in Leduc, Alberta — serving Edmonton and shipping across Canada."
        path="/services"
        jsonLd={[breadcrumbSchema([["Home", "/"], ["Services", "/services"]])]}
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <span className="font-mono-tech text-xs uppercase tracking-[0.35em] text-[#C87455]">Services · Leduc, Alberta</span>
          <h1 className="font-serif-display font-light text-4xl sm:text-5xl lg:text-6xl text-white mt-4 tracking-tight">
            Custom laser engraving &amp; UV printing <span className="italic text-[#A9B0B7]">for Leduc and Edmonton</span>
          </h1>
          <p className="text-zinc-400 mt-6 max-w-2xl leading-relaxed">
            Ember &amp; Steel Studio personalises drinkware, gifts, metal products and corporate merchandise in-house in Leduc, Alberta.
            We serve {SERVICE_AREAS.slice(0, -1).join(", ")} and {SERVICE_AREAS.at(-1)}, and ship finished work across Canada.
            Choose a service below to see what we make, what it costs and how to order.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900 mt-16">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.04}>
              <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`} className="group block bg-[#080808] hover:bg-[#0C0C0C] transition-colors duration-500 p-8 md:p-10 h-full">
                <div className="flex items-start justify-between gap-4">
                  <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#C87455]">0{i + 1}</span>
                  <ArrowUpRight size={16} className="text-zinc-600 group-hover:text-[#C87455] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </div>
                <h2 className="font-serif-display text-2xl md:text-3xl text-white mt-6">{s.h1}</h2>
                <p className="text-zinc-400 text-sm leading-relaxed mt-4 line-clamp-3">{s.intro}</p>
                <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-6">{s.materials.slice(0, 3).join(" · ")}</p>
              </Link>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-20 border border-zinc-900 bg-[#0A0A0A] p-8 md:p-12 grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              ["Order online", "Shop products are personalised at the product page and paid securely by card. Ships across Canada and to the USA by Canada Post — shipping is calculated at checkout.", "/shop", "Browse the shop"],
              ["Request a quote", "One-offs, bulk runs, your own items or anything not in the shop. Describe the job, attach a logo or photo, and we reply with options and a price within one working day.", "/quote", "Start a quote"],
              ["Corporate gifting", "Coordinated gift packages and volume pricing for teams and clients, quoted to your headcount and budget.", "/business", "See corporate packages"],
            ].map(([h, body, to, label]) => (
              <div key={h}>
                <h3 className="font-serif-display text-2xl text-white">{h}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed mt-3">{body}</p>
                <Link to={to} className="link-line inline-block mt-4 font-mono-tech text-xs uppercase tracking-[0.2em] text-[#C87455]" data-testid={`services-cta-${to.replace("/", "") || "home"}`}>{label} →</Link>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </main>
  );
}
